import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import {
  createShareLink,
  getDocument,
  revokeShareLink,
  updateDocument,
} from "@/lib/documents.functions";
import { SiteShell } from "@/components/site-shell";
import { PrintableDocument } from "@/components/printable-document";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/hooks/use-auth";
import { ArrowLeft, Download, Link2, Pencil, Save } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/documents/$id")({
  component: DocumentPage,
});

function DocumentPage() {
  const { id } = useParams({ from: "/_authenticated/documents/$id" });
  const { session } = useAuth();
  const headers = session?.access_token
    ? { Authorization: `Bearer ${session.access_token}` }
    : undefined;
  const get = useServerFn(getDocument);
  const save = useServerFn(updateDocument);
  const share = useServerFn(createShareLink);
  const unshare = useServerFn(revokeShareLink);
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: ["doc", id],
    queryFn: () => get({ data: { id }, headers }),
    enabled: !!headers,
  });

  const [editing, setEditing] = useState(false);
  const [proposalDraft, setProposalDraft] = useState("");
  const [contractDraft, setContractDraft] = useState("");
  const [saving, setSaving] = useState(false);

  const doc = q.data;

  function startEditing() {
    if (!doc) return;
    setProposalDraft(doc.proposal_content ?? "");
    setContractDraft(doc.contract_content ?? "");
    setEditing(true);
  }

  async function onSave() {
    if (!headers) return;
    setSaving(true);
    try {
      await save({
        data: {
          id,
          proposal_content: proposalDraft,
          contract_content: contractDraft,
        },
        headers,
      });
      await qc.invalidateQueries({ queryKey: ["doc", id] });
      setEditing(false);
      toast.success("Changes saved");
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function onShare() {
    if (!headers) return;
    try {
      const result = await share({ data: { id }, headers });
      await qc.invalidateQueries({ queryKey: ["doc", id] });
      const url = `${window.location.origin}${result.sharePath}`;
      await navigator.clipboard.writeText(url);
      toast.success("Share link copied to clipboard");
    } catch (err) {
      toast.error((err as Error).message);
    }
  }

  async function onRevokeShare() {
    if (!headers) return;
    try {
      await unshare({ data: { id }, headers });
      await qc.invalidateQueries({ queryKey: ["doc", id] });
      toast.success("Share link revoked");
    } catch (err) {
      toast.error((err as Error).message);
    }
  }

  if (q.isLoading)
    return (
      <SiteShell>
        <div className="p-10 text-sm text-muted-foreground">Loading…</div>
      </SiteShell>
    );

  if (!doc)
    return (
      <SiteShell>
        <div className="p-10">Not found</div>
      </SiteShell>
    );

  const shareUrl = doc.share_token
    ? `${typeof window !== "undefined" ? window.location.origin : ""}/share/${doc.share_token}`
    : null;

  return (
    <SiteShell>
      <section className="mx-auto max-w-3xl px-6 py-10 print:hidden">
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> Back to dashboard
        </Link>
        <h1 className="mt-3 text-3xl font-semibold tracking-tight">{doc.project_title}</h1>
        <p className="text-sm text-muted-foreground">
          For {doc.client_name} · ${Number(doc.total_price).toLocaleString()}
        </p>

        <Tabs defaultValue="proposal" className="mt-6">
          <TabsList>
            <TabsTrigger value="proposal">📄 Proposal</TabsTrigger>
            <TabsTrigger value="contract">📋 Contract</TabsTrigger>
          </TabsList>
          <TabsContent value="proposal">
            {editing ? (
              <Textarea
                className="mt-4 min-h-[320px] font-mono text-sm"
                value={proposalDraft}
                onChange={(e) => setProposalDraft(e.target.value)}
                aria-label="Edit proposal"
              />
            ) : (
              <pre className="mt-4 whitespace-pre-wrap rounded-xl border border-border bg-card p-6 text-sm leading-relaxed shadow-sm">
                {doc.proposal_content}
              </pre>
            )}
          </TabsContent>
          <TabsContent value="contract">
            {editing ? (
              <Textarea
                className="mt-4 min-h-[320px] font-mono text-sm"
                value={contractDraft}
                onChange={(e) => setContractDraft(e.target.value)}
                aria-label="Edit contract"
              />
            ) : (
              <pre className="mt-4 whitespace-pre-wrap rounded-xl border border-border bg-card p-6 text-sm leading-relaxed shadow-sm">
                {doc.contract_content}
              </pre>
            )}
          </TabsContent>
        </Tabs>

        <div className="no-print mt-6 flex flex-wrap gap-2">
          {editing ? (
            <>
              <Button onClick={onSave} disabled={saving} data-testid="save-document">
                <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save changes"}
              </Button>
              <Button variant="outline" onClick={() => setEditing(false)}>
                Cancel
              </Button>
            </>
          ) : (
            <Button variant="outline" onClick={startEditing} data-testid="edit-document">
              <Pencil className="h-4 w-4" /> Edit document
            </Button>
          )}
          <Button onClick={() => window.print()}>
            <Download className="h-4 w-4" /> Download as PDF
          </Button>
          {shareUrl ? (
            <>
              <Button variant="secondary" asChild>
                <a href={shareUrl} target="_blank" rel="noreferrer" data-testid="open-share-link">
                  <Link2 className="h-4 w-4" /> Open share link
                </a>
              </Button>
              <Button variant="ghost" onClick={onRevokeShare} data-testid="revoke-share-link">
                Revoke share link
              </Button>
            </>
          ) : (
            <Button variant="secondary" onClick={onShare} data-testid="create-share-link">
              <Link2 className="h-4 w-4" /> Create share link
            </Button>
          )}
          <Button
            variant="outline"
            disabled
            title="E-Signature is coming soon. Upgrade to Pro to get early access."
            className="text-muted-foreground"
          >
            ✍ Request E-Signature — Coming Soon
          </Button>
        </div>
      </section>

      <PrintableDocument
        proposal={editing ? proposalDraft : (doc.proposal_content ?? "")}
        contract={editing ? contractDraft : (doc.contract_content ?? "")}
        meta={{
          projectTitle: doc.project_title,
          clientName: doc.client_name,
          freelancerName: doc.freelancer_name,
          projectType: doc.project_type,
          totalPrice: doc.total_price,
          startDate: doc.start_date,
          endDate: doc.end_date,
        }}
      />
    </SiteShell>
  );
}
